import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Form,
  Button,
  InputGroup,
  Row,
  Col,
  Spinner,
  Alert,
  DatePicker,
} from "react-bootstrap";
import moment from "moment";
import axiosClient from "../api/axiosClient";
import { API_BASE_URL } from "../config/api";

const FinancialFunnel = () => {
  const [financialData, setFinancialData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [startDate, setStartDate] = useState(
    moment().subtract(30, "days").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [adSpendInputs, setAdSpendInputs] = useState({});
  const [editingRow, setEditingRow] = useState(null);

  // Helper function to check if a date is today
  const isToday = (dateString) => {
    return moment(dateString).isSame(moment(), "day");
  };

  // Fetch financial data for the date range
  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axiosClient.get(
        "https://api.viralgpt.com/admin/financial-range",
        {
          params: { startDate, endDate },
        },
      );

      const inputs = {};
      response.data.forEach((item) => {
        // Store the raw ad spend without the fee
        inputs[item.date] = item.adSpend / 1.1;
      });
      setAdSpendInputs(inputs);

      // Process data to separate ad fees
      const processedData = response.data.map((item) => {
        const rawAdSpend = item.adSpend / 1.1; // Remove the 10% fee to get the raw ad spend
        const adFees = rawAdSpend * 0.1; // Calculate the 10% fee separately

        return {
          ...item,
          adSpend: rawAdSpend, // Store the raw ad spend
          adFees: adFees, // Add the ad fees as a separate field
        };
      });

      // Sort data with today's date first, then remaining dates in descending order
      const today = moment().format("YYYY-MM-DD");
      const sortedData = [...processedData].sort((a, b) => {
        if (a.date === today) return -1;
        if (b.date === today) return 1;
        return moment(b.date).diff(moment(a.date)); // Descending order for other dates
      });

      setFinancialData(sortedData);
    } catch (error) {
      console.error("Error fetching financial data:", error);
      setError("Failed to load financial data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Update ad spend for a specific date
  const updateAdSpend = async (date) => {
    try {
      setError("");

      const adSpend = parseFloat(adSpendInputs[date]);
      if (isNaN(adSpend)) {
        setError("Please enter a valid number for ad spend.");
        return;
      }

      // We still send the total amount (ad spend + 10% fee) to the backend
      const adSpendWithFee = adSpend * 1.1;

      await axiosClient.post("https://api.viralgpt.com/admin/update-ad-spend", {
        date,
        adSpend: adSpendWithFee,
      });

      setSuccess(`Ad spend updated successfully for ${date}`);
      setTimeout(() => setSuccess(""), 3000);

      // Refresh data to show updated calculations
      fetchFinancialData();
      setEditingRow(null);
    } catch (error) {
      console.error("Error updating ad spend:", error);
      setError("Failed to update ad spend. Please try again.");
    }
  };

  // Handle input change for ad spend
  const handleAdSpendChange = (date, value) => {
    setAdSpendInputs((prev) => ({
      ...prev,
      [date]: value,
    }));
  };

  // Calculate totals for the footer
  const calculateTotals = () => {
    if (!financialData.length) return {};

    return financialData.reduce((totals, day) => {
      return {
        revenue: (totals.revenue || 0) + (parseFloat(day.revenue) || 0),
        fbRevenue: (totals.fbRevenue || 0) + (parseFloat(day.fbRevenue) || 0),
        grossRevenue:
          (totals.grossRevenue || 0) + (parseFloat(day.grossRevenue) || 0),
        productCosts:
          (totals.productCosts || 0) + (parseFloat(day.productCosts) || 0),
        adSpend: (totals.adSpend || 0) + (parseFloat(day.adSpend) || 0),
        adFees: (totals.adFees || 0) + (parseFloat(day.adFees) || 0),
        fees: (totals.fees || 0) + (parseFloat(day.fees) || 0),
        netRevenue:
          (totals.netRevenue || 0) + (parseFloat(day.netRevenue) || 0),
        newSubscribers:
          (totals.newSubscribers || 0) + (parseInt(day.newSubscribers) || 0),
      };
    }, {});
  };

  // Format currency values
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  // Initial data fetch
  useEffect(() => {
    fetchFinancialData();
  }, [startDate, endDate]);

  const totals = calculateTotals();

  return (
    <Card className="mb-4">
      <Card.Body className="p-0">
        {" "}
        {/* Reduce padding on mobile */}
        <Card.Title className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 p-3">
          <span>Financial Funnel</span>
          <div className="d-flex gap-2 mt-2 mt-sm-0 w-100 w-sm-auto">
            <Form.Group as={Row} className="align-items-center w-100">
              <Form.Label column sm="auto">
                Date Range:
              </Form.Label>
              <Col sm="auto">
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Col>
              <Col sm="auto">to</Col>
              <Col sm="auto">
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Col>
              <Col sm="auto">
                <Button variant="outline-primary" onClick={fetchFinancialData}>
                  Refresh
                </Button>
              </Col>
            </Form.Group>
          </div>
        </Card.Title>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover className="financial-table">
              <thead className="bg-light">
                <tr>
                  <th>Date</th>
                  <th>Initial Revenue | Sub Revenue</th>
                  <th>FB Revenue</th>
                  <th>Gross Revenue</th>
                  <th>Boost Costs</th>
                  <th>Ad Spend</th>
                  <th>Ad Fees</th>
                  <th>Main Fees</th>
                  <th>Net Revenue</th>
                  <th>New Subscribers</th>
                </tr>
              </thead>
              <tbody>
                {financialData.map((day) => (
                  <tr
                    key={day.date}
                    className={isToday(day.date) ? "today-highlight" : ""}
                  >
                    <td>
                      {isToday(day.date) ? (
                        <span className="today-label">
                          {moment(day.date).format("MMM DD, YYYY")}
                        </span>
                      ) : (
                        moment(day.date).format("MMM DD, YYYY")
                      )}
                    </td>
                    <td>
                      Organic: {formatCurrency(day.initialRevenue)} <br />
                      Renewal:{" "}
                      {formatCurrency(day.subscriptionRevenue)}
                    </td>
                    <td>{formatCurrency(day.fbRevenue)}</td>
                    <td style={{ backgroundColor: 'yellow' }}>{formatCurrency(day.grossRevenue)}</td>
                    <td>{formatCurrency(day.productCosts)}</td>
                    <td>
                      {editingRow === day.date ? (
                        <InputGroup size="sm">
                          <InputGroup.Text>$</InputGroup.Text>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={adSpendInputs[day.date] || ""}
                            onChange={(e) =>
                              handleAdSpendChange(day.date, e.target.value)
                            }
                            onBlur={() => {
                              if (adSpendInputs[day.date]) {
                                // Only save if there's a value
                                updateAdSpend(day.date);
                              } else {
                                setEditingRow(null); // Just exit edit mode if no value
                              }
                            }}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                if (adSpendInputs[day.date]) {
                                  // Only save if there's a value
                                  updateAdSpend(day.date);
                                } else {
                                  setEditingRow(null); // Just exit edit mode if no value
                                }
                              }
                            }}
                            autoFocus
                          />
                        </InputGroup>
                      ) : (
                        <div
                          onClick={() => setEditingRow(day.date)}
                          className="editable-cell"
                        >
                          {formatCurrency(day.adSpend)}
                        </div>
                      )}
                    </td>
                    <td>{formatCurrency(day.adFees)}</td>
                    <td>{formatCurrency(day.fees)}</td>
                    <td
                      style={{
                        backgroundColor: day.netRevenue >= 0 ? 'lime' : 'red'
                      }}
                    >
                      <strong>{formatCurrency(day.netRevenue)}</strong>
                    </td>
                    <td
                      className={
                        day.newSubscribers > 0 ? "new-subscribers" : ""
                      }
                    >
                      {day.newSubscribers}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-dark">
                <tr>
                  <td>
                    <strong>TOTALS</strong>
                  </td>
                  <td>{formatCurrency(totals.revenue)}</td>
                  <td>{formatCurrency(totals.fbRevenue)}</td>
                  <td>{formatCurrency(totals.grossRevenue)}</td>
                  <td>{formatCurrency(totals.productCosts)}</td>
                  <td>{formatCurrency(totals.adSpend)}</td>
                  <td>{formatCurrency(totals.adFees)}</td>
                  <td>{formatCurrency(totals.fees)}</td>
                  <td
                    className={
                      totals.netRevenue >= 0 ? "text-success" : "text-danger"
                    }
                  >
                    <strong>{formatCurrency(totals.netRevenue)}</strong>
                  </td>
                  <td>{totals.newSubscribers}</td>
                </tr>
              </tfoot>
            </Table>
            <style jsx="true">{`
              .today-label {
                font-weight: bold;
                color: #daa520;
              }
              td.new-subscribers {
                font-weight: bold;
                color: #daa520;
              }
              .editable-cell {
                cursor: pointer;
                padding: 4px 8px;
                border-radius: 4px;
                transition: background-color 0.2s;
              }
              .editable-cell:hover {
                background-color: rgba(0, 0, 0, 0.05);
              }

              /* Mobile adjustments to show all columns without horizontal scrolling */
              @media (max-width: 576px) {
                /* Remove horizontal scrolling */
                .table-responsive {
                  overflow-x: visible !important;
                }
                /* Force the table to fit within the screen */
                .financial-table {
                  table-layout: fixed;
                  width: 100%;
                  font-size: 0.4rem;
                }
                .financial-table th,
                .financial-table td {
                  padding: 0;
                  text-align: center;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: clip;
                }
              }
            `}</style>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default FinancialFunnel;
